import React, { useState } from 'react';
import { app, auth, database, storage } from '../../config/firebase';
import { ref as dbRef, push, set } from 'firebase/database';
import InferenceLearning from './InferenceLearning';
import { useTranslations } from '../../translations';
import { UserAuth } from '../../context/AuthContext';
import { useEntitlement } from '../../hooks/useEntitlement';
import AiPaywall from '../../components/protected/AiPaywall';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
});

const levelsTexts = ["crear", "evaluar", "analizar", "aplicar", "entender", "recordar"];

// Etiquetas localizadas para los metadatos que se guardan en la lección.
// El valor interno (clave) se sigue enviando a la IA, pero lo que se PERSISTE
// y lo que consume la app móvil debe estar en el idioma de la lección.
const difficultyLabels = {
  "fácil":   { es: "Fácil",   en: "Easy",      ta: "எளிது" },
  "medio":   { es: "Medio",   en: "Medium",    ta: "நடுத்தரம்" },
  "difícil": { es: "Difícil", en: "Difficult", ta: "கடினம்" },
  "mixto":   { es: "Mixto",   en: "Mixed",     ta: "கலப்பு" },
};

const levelLabels = {
  crear:    { es: "Crear",    en: "Create",     ta: "உருவாக்கு" },
  evaluar:  { es: "Evaluar",  en: "Evaluate",   ta: "மதிப்பீடு செய்" },
  analizar: { es: "Analizar", en: "Analyze",    ta: "பகுப்பாய்வு செய்" },
  aplicar:  { es: "Aplicar",  en: "Apply",      ta: "பயன்படுத்து" },
  entender: { es: "Entender", en: "Understand", ta: "புரிந்துகொள்" },
  recordar: { es: "Recordar", en: "Remember",   ta: "நினைவில் கொள்" },
};

const localizeDifficulty = (value, lang) =>
  difficultyLabels[value]?.[lang] || difficultyLabels[value]?.es || value;

const localizeLevel = (value, lang) =>
  levelLabels[value]?.[lang] || levelLabels[value]?.es || value;

const PAYWALL_ENFORCE = process.env.REACT_APP_AI_PAYWALL_ENFORCE === "true";

const InferenceLearningContainer = () => {
  const { t, language } = useTranslations();
  const { user } = UserAuth();
  const entitlement = useEntitlement();
  
  const [idea, setIdea] = useState("");
  const [numQuestions, setNumQuestions] = useState(3);
  const [numImageQuestions, setNumImageQuestions] = useState(3);
  const [selectedLang, setSelectedLang] = useState(language || "es");
  const [difficulty, setDifficulty] = useState("-1");
  const [imageDifficulty, setImageDifficulty] = useState("-1");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Bloom's taxonomy levels
  const [checkedLevels, setCheckedLevels] = useState([]);
  const [isCheckedLevels, setIsCheckedLevels] = useState(new Array(6).fill(false));

  const handleCheckLevels = (event, index) => {
    let updatedList = [...checkedLevels];
    const updatedCheckboxes = [...isCheckedLevels];

    if (event.target.checked) {
      updatedList[index] = levelsTexts[index];
      updatedCheckboxes[index] = true;
    } else {
      updatedList[index] = undefined;
      updatedCheckboxes[index] = false;
    }
    setCheckedLevels(updatedList);
    setIsCheckedLevels(updatedCheckboxes);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!idea.trim()) return;

    // Validations
    if (difficulty === "-1" || imageDifficulty === "-1") {
      Toast.fire({ icon: 'error', title: t("selectDifficultyFirst") || 'Seleccione las dificultades' });
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const bloomsUrl = process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms";
      const fetchUrl = bloomsUrl.replace("/blooms", "/inference");

      // Build levels string from selected checkboxes
      const selectedLevels = checkedLevels.filter(Boolean);
      const levelsString = selectedLevels.length > 0 ? selectedLevels.toString() : "";

      // Token de Firebase para el gate de entitlement de IA (Fase 0).
      const idToken = auth.currentUser ? await auth.currentUser.getIdToken() : null;

      const reqOptions = {
        method: "POST",
        body: JSON.stringify({
          idea: idea,
          numQuestions: Number(numQuestions),
          numImageQuestions: Number(numImageQuestions),
          language: selectedLang,
          difficulty: difficulty,
          imageDifficulty: imageDifficulty,
          levels: levelsString,
          key: process.env.REACT_APP_BLOOMS_API_KEY
        }),
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        }
      };

      const response = await fetch(fetchUrl, reqOptions);
      if (!response.ok) {
        throw new Error(`Error en el servidor de Vercel: ${response.statusText}`);
      }

      const responseData = await response.json();

      if (responseData && responseData.success) {
        setResult(responseData.data);
      } else {
        throw new Error(responseData?.message || 'Error desconocido al invocar la API Vercel');
      }

    } catch (error) {
      console.error("Error generating:", error);
      Toast.fire({
        icon: 'error',
        title: t("errorGenerating") || 'Ocurrió un error al generar el contenido.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!result || !user || !user.uid) return;
    
    setSaving(true);
    Toast.fire({
      icon: 'info',
      title: t("savingImage")
    });

    try {
      let finalImageUrl = result.imageUrl;

      // Las imágenes de DALL-E (Azure Blob) expiran y no permiten CORS directo, 
      // así que usamos un proxy CORS de puente para descargarla y subir a Cloudinary
      if (finalImageUrl && !finalImageUrl.includes('cloudinary')) {
        const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(finalImageUrl)}&w=1024`;
        const imgResponse = await fetch(proxyUrl);
        
        if (!imgResponse.ok) {
          throw new Error("No se pudo descargar la imagen origen por reglas de red.");
        }
        
        const imgBlob = await imgResponse.blob();
        
        // Subir a Cloudinary (Unsigned Preset)
        const formData = new FormData();
        formData.append("file", imgBlob);
        formData.append("upload_preset", "miabc_online");

        const cloudinaryResponse = await fetch("https://api.cloudinary.com/v1_1/dbohppph0/image/upload", {
          method: "POST",
          body: formData,
        });

        if (!cloudinaryResponse.ok) {
          throw new Error("No se pudo guardar la imagen en Cloudinary.");
        }
        
        const cloudinaryData = await cloudinaryResponse.json();
        finalImageUrl = cloudinaryData.secure_url;
      }

      // Build selected levels for storage, localized to the lesson language
      const selectedLevels = checkedLevels
        .filter(Boolean)
        .map((lvl) => localizeLevel(lvl, selectedLang));

      const finalResult = {
        ...result,
        imageUrl: finalImageUrl,
        difficulty: localizeDifficulty(difficulty, selectedLang),
        levels: selectedLevels,
      };

      const newInferenceRef = push(dbRef(database, `users/${user.uid}/inference_lessons`));
      // Firebase set() rechaza cualquier `undefined`; saneamos para no perder el guardado.
      await set(newInferenceRef, JSON.parse(JSON.stringify(finalResult)));
      
      Toast.fire({
        icon: 'success',
        title: t("savedSuccessfully") || 'Contenido guardado exitosamente'
      });
      handleReset();
    } catch (error) {
      console.error("Error guardando:", error);
      Toast.fire({
        icon: 'error',
        title: t("errorSaving") || 'Error al intentar guardar'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setIdea("");
    setNumQuestions(3);
    setNumImageQuestions(3);
    setDifficulty("-1");
    setImageDifficulty("-1");
    setResult(null);
    setCheckedLevels([]);
    setIsCheckedLevels(new Array(6).fill(false));
  };

  // Paywall: solo bloquea si el flag está activo (alineado con AI_GATE_MODE=enforce del backend).
  if (PAYWALL_ENFORCE && !entitlement.loading && !entitlement.ai) {
    return <AiPaywall />;
  }

  return (
    <InferenceLearning
      idea={idea}
      setIdea={setIdea}
      numQuestions={numQuestions}
      setNumQuestions={setNumQuestions}
      numImageQuestions={numImageQuestions}
      setNumImageQuestions={setNumImageQuestions}
      selectedLang={selectedLang}
      setSelectedLang={setSelectedLang}
      difficulty={difficulty}
      setDifficulty={setDifficulty}
      imageDifficulty={imageDifficulty}
      setImageDifficulty={setImageDifficulty}
      isCheckedLevels={isCheckedLevels}
      handleCheckLevels={handleCheckLevels}
      loading={loading}
      saving={saving}
      result={result}
      handleGenerate={handleGenerate}
      handleSave={handleSave}
      handleReset={handleReset}
      t={t}
    />
  );
};

export default InferenceLearningContainer;
